/**
 * Interpolation of remote entities (DESIGN 5.6 "Everyone else" and
 * "Beams/sprays/arcs/hazards"): turns the two snapshots around a render
 * time into View* entities with a render pose, extrapolating with the
 * snapshot velocities for up to CLIENT.extrapolateMaxMs and holding after.
 *
 * Pure functions over the SnapshotBuffer; ClientState overrides the local
 * player's pose with its prediction afterwards.
 */

import type {
  BeamSnap,
  EnemyDef,
  LobbyPlayer,
  PlayerSnap,
  SnapMsg,
  Vec2,
} from '@shared/index.js';
import { BEAM, CLIENT, ENEMY_DEFS, fromAngle, lerpAngle } from '@shared/index.js';
import type { SnapshotBuffer } from '../net/snapshotBuffer.js';
import type {
  ViewArc,
  ViewBeam,
  ViewCorpse,
  ViewEnemy,
  ViewHazard,
  ViewMarker,
  ViewPlayer,
  ViewProjectile,
  ViewSpray,
} from './viewModel.js';

export interface InterpContext {
  /** Local player id (-1 before welcome). */
  pid: number;
  /** Room roster by player id, for names and colours. */
  roster: ReadonlyMap<number, LobbyPlayer>;
  /** Server ms when each currently-dead player died (ViewCorpse.since). */
  deadSince: ReadonlyMap<number, number>;
}

export interface InterpolatedEntities {
  players: ViewPlayer[];
  enemies: ViewEnemy[];
  projectiles: ViewProjectile[];
  corpses: ViewCorpse[];
}

export interface SpellViews {
  beams: ViewBeam[];
  sprays: ViewSpray[];
  arcs: ViewArc[];
  hazards: ViewHazard[];
}

/** The local player's instant aim for beam override (DESIGN 5.6). */
export interface LocalBeamAim {
  /** Predicted position of the local player. */
  pos: Vec2;
  /** Local aim in radians. */
  aim: number;
  /** Server ms when each beam id was first seen in a snapshot. */
  beamSince: ReadonlyMap<number, number>;
  /** Estimated current server ms. */
  serverTime: number;
}

interface Kinematic { x: number; y: number; vx: number; vy: number }

/** Fallback body radius when ENEMY_DEFS has no row yet (same as a player). */
const DEFAULT_ENEMY_RADIUS = 0.45;

/**
 * Pose of one body: linear between a and b, or a + v * dt past the newest
 * snapshot (dt already capped by the buffer, so the hold is implicit).
 */
export function interpolatePose(a: Kinematic, b: { x: number; y: number } | null, alpha: number, extrapolateMs: number): Vec2 {
  if (b) {
    return { x: a.x + (b.x - a.x) * alpha, y: a.y + (b.y - a.y) * alpha };
  }
  const dt = extrapolateMs / 1000;
  return { x: a.x + a.vx * dt, y: a.y + a.vy * dt };
}

function byId<T extends { id: number }>(list: readonly T[]): Map<number, T> {
  const m = new Map<number, T>();
  for (const e of list) m.set(e.id, e);
  return m;
}

function emptyEntities(): InterpolatedEntities {
  return { players: [], enemies: [], projectiles: [], corpses: [] };
}

/**
 * Every body in snapshot `a` interpolated toward `b`; bodies that exist only
 * in `b` (spawned between the two) are shown held at their `b` pose rather
 * than popping in up to 100 ms later, which keeps them in step with the
 * cast/explosion events the client plays on receipt.
 */
export function interpolateEntities(buffer: SnapshotBuffer, renderTime: number, ctx: InterpContext): InterpolatedEntities {
  const s = buffer.sample(renderTime);
  if (!s) return emptyEntities();
  const { a, b, alpha, extrapolateMs } = s;
  const out = emptyEntities();

  const bPlayers = b ? byId(b.p) : null;
  for (const p of a.p) {
    const nb = bPlayers?.get(p.id) ?? null;
    const pos = interpolatePose(p, nb, alpha, extrapolateMs);
    const facing = nb ? lerpAngle(p.aim, nb.aim, alpha) : p.aim;
    pushPlayer(out, p, pos, facing, ctx, a.st);
  }
  if (b && bPlayers) {
    const seen = new Set(a.p.map((p) => p.id));
    for (const p of b.p) if (!seen.has(p.id)) pushPlayer(out, p, { x: p.x, y: p.y }, p.aim, ctx, b.st);
  }

  const bEnemies = b ? byId(b.e) : null;
  for (const e of a.e) {
    const nb = bEnemies?.get(e.id) ?? null;
    const pos = interpolatePose(e, nb, alpha, extrapolateMs);
    const facing = nb ? lerpAngle(e.f, nb.f, alpha) : e.f;
    out.enemies.push({ ...e, rx: pos.x, ry: pos.y, facing, boss: isBoss(e.k), radius: enemyRadius(e.k) });
  }
  if (b && bEnemies) {
    const seen = new Set(a.e.map((e) => e.id));
    for (const e of b.e) if (!seen.has(e.id)) out.enemies.push({ ...e, rx: e.x, ry: e.y, facing: e.f, boss: isBoss(e.k), radius: enemyRadius(e.k) });
  }

  const bProj = b ? byId(b.pr) : null;
  for (const pr of a.pr) {
    const nb = bProj?.get(pr.id) ?? null;
    const pos = interpolatePose(pr, nb, alpha, extrapolateMs);
    const view: ViewProjectile = { ...pr, rx: pos.x, ry: pos.y, facing: heading(pr.vx, pr.vy), isLocal: pr.o === ctx.pid };
    // a thrown rock's height glides along its arc between snapshots too
    if (pr.z !== undefined && nb?.z !== undefined) view.z = pr.z + (nb.z - pr.z) * alpha;
    out.projectiles.push(view);
  }
  if (b && bProj) {
    const seen = new Set(a.pr.map((p) => p.id));
    for (const pr of b.pr) if (!seen.has(pr.id)) out.projectiles.push({ ...pr, rx: pr.x, ry: pr.y, facing: heading(pr.vx, pr.vy), isLocal: pr.o === ctx.pid });
  }
  return out;
}

/**
 * Field markers (placed Magicks, boss tells) at the render time, like the
 * bodies they act on: a walking tornado or elemental glides between
 * snapshots, and every clock (`t`, `act`) runs smoothly, which is what a
 * meteor's fall and a tell's growing ring are drawn from. Markers that
 * exist only in the newer snapshot show at once (a tell must not lag).
 */
export function interpolateMarkers(buffer: SnapshotBuffer, renderTime: number, pid: number): ViewMarker[] {
  const s = buffer.sample(renderTime);
  if (!s) return [];
  const { a, b, alpha, extrapolateMs } = s;
  const aList = a.mk ?? [];
  const bList = b?.mk ?? [];
  const bById = byId(bList);
  const out: ViewMarker[] = [];
  const ahead = extrapolateMs / 1000;
  for (const m of aList) {
    const nb = bById.get(m.id) ?? null;
    const view: ViewMarker = nb
      ? { ...m, rx: m.x + (nb.x - m.x) * alpha, ry: m.y + (nb.y - m.y) * alpha, ang: lerpAngle(m.ang, nb.ang, alpha), t: m.t + (nb.t - m.t) * alpha, el: nb.el, isLocal: m.o === pid }
      : { ...m, rx: m.x, ry: m.y, t: Math.max(0, m.t - ahead), isLocal: m.o === pid };
    if (view.act !== undefined) view.act = nb?.act !== undefined ? view.act + (nb.act - view.act) * alpha : view.act + ahead;
    out.push(view);
  }
  const seen = new Set(aList.map((m) => m.id));
  for (const m of bList) if (!seen.has(m.id)) out.push({ ...m, rx: m.x, ry: m.y, isLocal: m.o === pid });
  return out;
}

function pushPlayer(out: InterpolatedEntities, p: PlayerSnap, pos: Vec2, facing: number, ctx: InterpContext, st: number): void {
  const roster = ctx.roster.get(p.id);
  const name = roster?.name ?? `Wizard ${p.id}`;
  const color = roster?.color ?? 0;
  const isLocal = p.id === ctx.pid;
  out.players.push({
    ...p,
    rx: pos.x,
    ry: pos.y,
    facing,
    isLocal,
    name,
    color,
    connected: p.con === 1,
  });
  // DESIGN 4.5: the corpse stays where the player died. The body layer should
  // draw corpses from here and skip players with dead = 1.
  if (p.dead === 1) {
    out.corpses.push({ id: p.id, name, color, isLocal, rx: pos.x, ry: pos.y, facing, since: ctx.deadSince.get(p.id) ?? st });
  }
}

function heading(vx: number, vy: number): number {
  return vx === 0 && vy === 0 ? 0 : Math.atan2(vy, vx);
}

function isBoss(kind: string): boolean {
  const def = (ENEMY_DEFS as Partial<Record<string, EnemyDef>>)[kind];
  return def?.traits.boss === true || kind === 'jotunn';
}

function enemyRadius(kind: string): number {
  const def = (ENEMY_DEFS as Partial<Record<string, EnemyDef>>)[kind];
  return def?.radius ?? DEFAULT_ENEMY_RADIUS;
}

/**
 * Beams, sprays, arcs and hazards straight from the newest snapshot (no
 * interpolation, DESIGN 5.6). A beam owned by the local player follows the
 * local aim for its first CLIENT.localBeamAimMs so the cast feels instant.
 * Rather than switching to the server angle at 100 ms (a visible pop), the
 * angle blends from local to server over that window; both end in the same
 * place and the blend reads as the beam "settling".
 */
export function spellViews(snap: SnapMsg | null, pid: number, local: LocalBeamAim | null): SpellViews {
  if (!snap) return { beams: [], sprays: [], arcs: [], hazards: [] };
  const beams: ViewBeam[] = snap.b.map((bm) => beamView(bm, pid, local));
  const sprays: ViewSpray[] = snap.s.map((sp) => ({ ...sp, rx: sp.x, ry: sp.y, facing: sp.ang, isLocal: sp.o === pid }));
  const arcs: ViewArc[] = snap.ar.map((ar) => ({ ...ar, isLocal: ar.o === pid }));
  const hazards: ViewHazard[] = snap.h.map((h) => ({ ...h, rx: h.x, ry: h.y, facing: h.ang, isLocal: h.o === pid }));
  return { beams, sprays, arcs, hazards };
}

function beamView(bm: BeamSnap, pid: number, local: LocalBeamAim | null): ViewBeam {
  const isLocal = bm.o === pid;
  if (isLocal && local) {
    const since = local.beamSince.get(bm.id);
    const age = since === undefined ? Number.POSITIVE_INFINITY : local.serverTime - since;
    if (age < CLIENT.localBeamAimMs) {
      const t = Math.max(0, age) / CLIENT.localBeamAimMs;
      const facing = lerpAngle(local.aim, bm.ang, t);
      const origin = fromAngle(facing, BEAM.offset);
      return { ...bm, rx: local.pos.x + origin.x, ry: local.pos.y + origin.y, facing, isLocal };
    }
  }
  return { ...bm, rx: bm.x, ry: bm.y, facing: bm.ang, isLocal };
}
